import { router } from "@inertiajs/react";

// Google Analytics runs on the public marketing pages only (see the
// SiteAnalytics controller concern). The layout adds the gtag.js script and
// a `google-analytics-id` meta tag on those full page loads; this file does
// the gtag bootstrap and keeps GA from ever seeing an app URL.
//
// Inertia navigates without reloading, and GA records every history change
// as a page view. So once GA is loaded, a visit to anything outside the
// marketing pages becomes a full page load instead, and the new document
// (which the server renders without GA) never has the tag at all. Moving
// between marketing pages stays an Inertia visit, and GA's own history
// tracking counts it.

// Paths that are safe to reach without a reload. Anything else, including
// the catch-all content pages, reloads and lets the server decide.
const PUBLIC_PATHS = [
  /^\/$/,
  /^\/readathon-software$/,
  /^\/pricing$/,
  /^\/how-it-works$/,
  /^\/about-us$/,
  /^\/contact(\/thanks)?$/,
  /^\/signup$/,
  /^\/(privacy|terms|dpa)$/,
  /^\/help(\/.*)?$/,
];

function isPublicPath(pathname) {
  return PUBLIC_PATHS.some((pattern) => pattern.test(pathname));
}

// A same-site referrer can be an app URL (a school or student page), so
// only its origin goes to GA.
function pageReferrer() {
  if (!document.referrer) return undefined;
  try {
    const referrer = new URL(document.referrer);
    return referrer.origin === window.location.origin
      ? `${referrer.origin}/`
      : document.referrer;
  } catch {
    return undefined;
  }
}

// Analytics cookies are on by default, except in Quebec, where Law 25
// requires them off until the visitor turns them on. Either way the
// visitor's own choice (from the footer's cookie settings) wins. Google
// finds the region from the visitor's IP; we never do. Ad storage is
// always off: GA here is for counting visits, not advertising.
const CHOICE_KEY = "pagepledge-analytics-cookies";
const OPT_IN_REGIONS = ["CA-QC"];
const ALWAYS_DENIED = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
};

// "granted", "denied", or null when the visitor hasn't chosen.
export function analyticsCookieChoice() {
  try {
    const choice = window.localStorage.getItem(CHOICE_KEY);
    return choice === "granted" || choice === "denied" ? choice : null;
  } catch {
    return null;
  }
}

export function setAnalyticsCookieChoice(choice) {
  try {
    window.localStorage.setItem(CHOICE_KEY, choice);
  } catch {
    // Storage blocked: the choice still applies until the page reloads.
  }
  window.gtag?.("consent", "update", { analytics_storage: choice });
  if (choice === "denied") deleteAnalyticsCookies();
}

// Denying consent stops new cookies but leaves the ones already set, so
// clear GA's cookies (_ga, _ga_<id>) on every domain level GA may use.
function deleteAnalyticsCookies() {
  const names = document.cookie
    .split(";")
    .map((cookie) => cookie.split("=")[0].trim())
    .filter((name) => name === "_ga" || name.startsWith("_ga_"));
  const parts = window.location.hostname.split(".");
  const domains = [""];
  for (let i = 0; i < parts.length - 1; i += 1) {
    domains.push(`; domain=.${parts.slice(i).join(".")}`);
  }
  for (const name of names) {
    for (const domain of domains) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
    }
  }
}

export function installSiteAnalytics() {
  const measurementId = document
    .querySelector('meta[name="google-analytics-id"]')
    ?.getAttribute("content");
  if (!measurementId) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag.js expects the arguments object itself, not an array.
    window.dataLayer.push(arguments);
  };

  // The region-specific default wins over the general one for Quebec.
  window.gtag("consent", "default", {
    ...ALWAYS_DENIED,
    analytics_storage: "denied",
    region: OPT_IN_REGIONS,
  });
  window.gtag("consent", "default", { ...ALWAYS_DENIED, analytics_storage: "granted" });
  const choice = analyticsCookieChoice();
  if (choice) window.gtag("consent", "update", { analytics_storage: choice });

  window.gtag("js", new Date());
  window.gtag("config", measurementId, {
    page_referrer: pageReferrer(),
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });

  router.on("before", (event) => {
    const { visit } = event.detail;
    if (visit.method !== "get" || isPublicPath(visit.url.pathname)) return;

    event.preventDefault();
    // A hover prefetch is wasted work when the click will reload anyway.
    if (!visit.prefetch) window.location.assign(visit.url.href);
  });

  // Backstop: a public URL that redirects (or a form post that lands)
  // somewhere private. Stop GA and reload so the private page renders in
  // a document without it.
  router.on("navigate", (event) => {
    if (event.detail.page.props.site_analytics) return;

    window[`ga-disable-${measurementId}`] = true;
    window.location.reload();
  });
}
